import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import type { Store } from '@warp-drive/core';
import { reloadRecord } from './save-record.ts';
import type { ObservableDocument } from '../data/resources/observable-document.type.ts';

/**
 * ObservableDocument action request builders.
 *
 * Replaces the duplicated `dispute` / `discardObservableDocument` methods on
 * the near-identical `tio-employee/app/adapters/observable-document.ts` and
 * `tio-admin/app/adapters/observable-document.ts`. Neither employee copy
 * consults `demoMode` (unlike some other domains in the inventory), so there
 * is NO demo seam to push to the call site here, and neither method carries a
 * `by:{name,userId,actingWithRole}` actor — dispute takes only `{ note }`.
 *
 * State-changing requests should be followed by `record.reload()` in the
 * caller (the legacy `dispute` adapter method folded that reload in; the
 * model method now does it explicitly — see
 * `addons/tio-data/src/models/observable-document.ts`).
 *
 * ADMIN actions (wave 3.2c): `reflect` / `rerun` / `restoreObservableDocument`
 * are single-app (admin) surface — added below for the wave-3.2c-ii call-site
 * conversion. All three are member POSTs with NO body; routes confirm them on
 * `:observable_documents` (config/routes.rb:278-280): `post 'reflect'`,
 * `post 'rerun'`, `post 'restore-observable-document'`.
 */

const observableDocumentsUrlOptions: QueryUrlOptions = {
  identifier: { type: 'observable-documents' },
  op: 'query',
  resourcePath: 'observable-documents',
};

function observableDocumentsUrl(path?: string): string {
  const base = buildBaseURL(observableDocumentsUrlOptions);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
}

/**
 * PATCH /observable-documents/:id/transition/subsistence-state/dispute
 * body `{ note }`
 *
 * Mirrors `ObservableDocumentAdapter#dispute`, which built the URL via
 * `this.buildURL('observable-document', id) + '/transition/subsistence-state/dispute'`
 * and sent `this.ajax(url, 'PATCH', { data: { note } })` (Ember Data's `data`
 * option IS the request body, not a JSON:API envelope).
 */
export function disputeObservableDocumentRequest(id: string, note: string): RequestInfo {
  return {
    url: observableDocumentsUrl(`${id}/transition/subsistence-state/dispute`),
    method: 'PATCH',
    body: JSON.stringify({ note }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Dispute an ObservableDocument and refresh the cached record. Replaces the
 * legacy `doc.dispute(note)` method on the extension — moved out because
 * WarpDrive resource extensions can't participate in Ember DI.
 */
export async function disputeObservableDocument(
  store: Store,
  doc: ObservableDocument,
  note: string
): Promise<ObservableDocument> {
  await store.request(disputeObservableDocumentRequest(doc.id as string, note));
  return reloadRecord(store, doc);
}

/**
 * DELETE /observable-documents/:id
 *
 * Mirrors `ObservableDocumentAdapter#discardObservableDocument`, which built
 * the URL via `this.buildURL('observable-documents') + '/' + id` and sent
 * `this.ajax(url, 'DELETE')` (no body).
 */
export function discardObservableDocumentRequest(id: string): RequestInfo {
  return {
    url: observableDocumentsUrl(id),
    method: 'DELETE',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /observable-documents/:id/reflect  (admin-only surface, NO body)
 *
 * Mirrors `ObservableDocumentAdapter#reflect` (tio-admin adapter), which built
 * the URL via `this.buildURL('observable-documents') + '/' + id + '/reflect'`
 * and sent `this.ajax(url, 'POST')` with no body. Route: config/routes.rb:278
 * `post 'reflect'` (member). No request body is emitted here either.
 */
export function reflectObservableDocumentRequest(id: string): RequestInfo {
  return {
    url: observableDocumentsUrl(`${id}/reflect`),
    method: 'POST',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /observable-documents/:id/rerun  (admin-only surface, NO body)
 *
 * Mirrors `ObservableDocumentAdapter#rerun` (tio-admin adapter), which built the
 * URL via `this.buildURL('observable-documents') + '/' + id + '/rerun'` and sent
 * `this.ajax(url, 'POST')` with no body. Route: config/routes.rb:279
 * `post 'rerun'` (member). No request body is emitted here either.
 */
export function rerunObservableDocumentRequest(id: string): RequestInfo {
  return {
    url: observableDocumentsUrl(`${id}/rerun`),
    method: 'POST',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * POST /observable-documents/:id/restore-observable-document  (admin-only, NO body)
 *
 * Mirrors `ObservableDocumentAdapter#restoreObservableDocument` (tio-admin
 * adapter), which built the URL via `this.buildURL('observable-documents') + '/'
 * + id + '/restore-observable-document'` and sent `this.ajax(url, 'POST')` with no
 * body. NB: the wire segment is the FULL `restore-observable-document`, NOT a
 * bare `restore` (route: config/routes.rb:280 `post 'restore-observable-document'`,
 * member), preserved exactly. No request body is emitted here either.
 */
export function restoreObservableDocumentRequest(id: string): RequestInfo {
  return {
    url: observableDocumentsUrl(`${id}/restore-observable-document`),
    method: 'POST',
    cacheOptions: { [SkipCache]: true },
  };
}
